import React, { useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@exiftrator/shared/components/ui";
import { formatForDateTimeLocal } from "../../utils/dateUtils";

const BASE_INPUT_CLASSES =
  "w-full h-8 px-2 bg-bg-tertiary border border-accent-primary/40 rounded-sm text-text-primary font-mono text-[11px] focus:outline-none focus:bg-bg-secondary transition-all";

export default function EditableCell({
  field,
  value,
  editSession,
  isModified,
  onSave,
  onCancel,
}) {
  const isEditing = Boolean(editSession);
  const resolvedEditValue = editSession?.replaceExisting
    ? (editSession.initialValue ?? "")
    : (value ?? "");

  const handleKeyDown = useCallback(
    (event, allowEnter = true) => {
      if (allowEnter && event.key === "Enter") {
        event.target.blur();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  if (!isEditing) {
    return (
      <span
        className={`inline-block w-full min-h-[20px] px-1 py-[2px] rounded-xs ${
          isModified ? "text-accent-primary font-semibold" : ""
        }`}
      >
        {field.formatForDisplay(value)}
      </span>
    );
  }

  const commonProps = {
    className: BASE_INPUT_CLASSES,
    autoFocus: true,
    onKeyDown: handleKeyDown,
  };

  switch (field.type) {
    case "datetime-local":
      return (
        <input
          {...commonProps}
          type="datetime-local"
          defaultValue={
            editSession?.replaceExisting ? "" : formatForDateTimeLocal(value)
          }
          onBlur={(event) => {
            const nextValue = event.target.value
              ? new Date(event.target.value).toISOString()
              : null;
            onSave(nextValue);
          }}
        />
      );

    case "gps-coordinate":
    case "number":
      return (
        <input
          {...commonProps}
          type="number"
          defaultValue={resolvedEditValue}
          step={field.type === "gps-coordinate" ? "0.000001" : "any"}
          onBlur={(event) => {
            const nextValue =
              event.target.value === "" ? null : parseFloat(event.target.value);
            onSave(nextValue);
          }}
        />
      );

    case "select": {
      const selectValue =
        typeof value === "object" && value !== null
          ? value.value
          : (value ?? undefined);

      return (
        <Select
          value={selectValue}
          defaultOpen={true}
          onValueChange={onSave}
          onOpenChange={(open) => {
            if (!open) {
              onCancel();
            }
          }}
        >
          <SelectTrigger
            className={BASE_INPUT_CLASSES}
            style={{
              height: "32px",
              fontSize: "11px",
              fontFamily: "ui-monospace, monospace",
            }}
          >
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    case "textarea":
      return (
        <textarea
          {...commonProps}
          className={`${BASE_INPUT_CLASSES} min-h-[60px] py-1`}
          defaultValue={resolvedEditValue}
          onBlur={(event) => onSave(event.target.value)}
          onKeyDown={(event) => handleKeyDown(event, false)}
          rows={2}
        />
      );

    default:
      return (
        <input
          {...commonProps}
          type="text"
          defaultValue={resolvedEditValue}
          onBlur={(event) => onSave(event.target.value)}
        />
      );
  }
}
